import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
// React 19 native metadata
import { ChevronRight, ArrowLeft, BookOpen, ArrowRight } from 'lucide-react';
import ShareButton from '../common/ShareButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GlossaryTermPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/glossary/${slug}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center text-white">
      <h1 className="text-2xl font-bold mb-4">Term not found</h1>
      <Link to="/glossary" className="text-purple-400 hover:text-purple-300">Back to Glossary</Link>
    </div>
  );

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    "name": data.term,
    "description": data.definition,
    "inLanguage": "en-US",
    "inDefinedTermSet": {
      "@type": "DefinedTermSet",
      "name": "AI Marketing Glossary",
      "url": "https://isupplyai.com/glossary",
    },
    "url": `https://isupplyai.com/glossary/${slug}`,
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://isupplyai.com" },
      { "@type": "ListItem", "position": 2, "name": "Glossary", "item": "https://isupplyai.com/glossary" },
      { "@type": "ListItem", "position": 3, "name": data.term, "item": `https://isupplyai.com/glossary/${slug}` }
    ]
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": `What is ${data.term}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": data.definition,
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="glossary-term-page">
      <>
        <title>{data.term.length > 30 ? `${data.term} — AI Glossary | iSupplyAI` : `${data.term} — AI Marketing Glossary | iSupplyAI`}</title>
        <meta name="description" content={data.definition.slice(0, 160)} />
        <link rel="canonical" href={`https://isupplyai.com/glossary/${slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="iSupplyAI" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content={`${data.term} — AI Marketing Glossary`} />
        <meta property="og:description" content={data.definition.slice(0, 160)} />
        <meta property="og:url" content={`https://isupplyai.com/glossary/${slug}`} />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@isupplyai" />
        <meta name="twitter:creator" content="@isupplyai" />
        <meta name="twitter:title" content={`${data.term} — AI Marketing Glossary`} />
        <meta name="twitter:description" content={data.definition.slice(0, 160)} />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </>

      {/* Breadcrumbs */}
      <div className="max-w-3xl mx-auto px-4 pt-8" data-testid="glossary-term-breadcrumbs">
        <nav className="flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-gray-300 transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/glossary" className="hover:text-gray-300 transition-colors">Glossary</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-300">{data.term}</span>
        </nav>
      </div>

      {/* Term Content */}
      <article className="max-w-3xl mx-auto px-4 pt-8 pb-12">
        <div className="flex items-center gap-2 mb-6">
          <span className="px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs font-medium" data-testid="glossary-term-category">
            {data.category}
          </span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold mb-8" data-testid="glossary-term-title">{data.term}</h1>

        {/* Definition */}
        <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/10 mb-8" data-testid="glossary-term-definition">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-4 h-4 text-purple-400" />
            <span className="text-xs font-semibold tracking-widest text-purple-400 uppercase">Definition</span>
          </div>
          <p className="text-gray-300 leading-relaxed text-base">{data.definition}</p>
        </div>

        {/* Example */}
        {data.example && (
          <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-900/15 to-cyan-900/10 border border-purple-500/15 mb-8" data-testid="glossary-term-example">
            <div className="text-xs font-semibold tracking-widest text-cyan-400 uppercase mb-3">Example</div>
            <p className="text-gray-300 leading-relaxed text-sm italic">{data.example}</p>
          </div>
        )}

        {/* Related Terms */}
        {data.related && data.related.length > 0 && (
          <div className="mb-8" data-testid="glossary-related-terms">
            <h2 className="text-lg font-bold mb-4">Related Terms</h2>
            <div className="flex flex-wrap gap-3">
              {data.related.map(r => (
                <Link key={r.slug} to={`/glossary/${r.slug}`} className="px-4 py-2 rounded-xl bg-white/[0.03] border border-white/10 hover:border-purple-500/30 text-sm text-gray-300 hover:text-purple-300 transition-all" data-testid={`related-term-${r.slug}`}>
                  {r.term}
                  <span className="ml-2 text-xs text-gray-600">{r.category}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Related Blog Posts */}
        {data.related_posts && data.related_posts.length > 0 && (
          <div className="mb-8" data-testid="glossary-related-posts">
            <h2 className="text-lg font-bold mb-4">Related Reading</h2>
            <div className="space-y-3">
              {data.related_posts.map(p => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group block p-4 rounded-xl bg-white/[0.02] border border-white/8 hover:border-purple-500/25 hover:bg-white/[0.04] transition-all" data-testid={`related-post-${p.slug}`}>
                  <div className="text-xs text-purple-400 mb-1">{p.category}</div>
                  <div className="text-sm font-medium text-white group-hover:text-purple-300 transition-colors">{p.title}</div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="p-8 rounded-2xl bg-gradient-to-br from-purple-900/20 to-cyan-900/10 border border-purple-500/20 text-center" data-testid="glossary-term-cta">
          <h2 className="text-xl font-bold mb-3">See {data.term} in action</h2>
          <p className="text-gray-400 text-sm mb-5">Experience how iSupplyAI uses AI marketing strategy to deliver results.</p>
          <Link to="/strategy-score" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-semibold rounded-xl text-sm hover:shadow-lg hover:shadow-purple-500/25 transition-all" data-testid="glossary-term-cta-btn">
            Get Your Free Strategy Score <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </article>

      {/* Back link */}
      <div className="max-w-3xl mx-auto px-4 pb-16">
        <Link to="/glossary" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-300 transition-colors" data-testid="back-to-glossary">
          <ArrowLeft className="w-4 h-4" /> Back to Glossary
        </Link>
      </div>
      <ShareButton title={`${data.term} — AI Marketing Glossary`} description={data.definition.slice(0, 160)} url={`https://isupplyai.com/glossary/${slug}`} />
    </div>
  );
}
